export type DataEntryField =
  | {
      name: string;
      label: string;
      type: 'text' | 'month' | 'date' | 'number' | 'textarea';
    }
  | {
      name: string;
      label: string;
      type: 'select';
      options: Array<{ value: string; label: string }>;
    };

export type SampleChartSpec = {
  title: string;
  categories: string[];
  yAxisTitle: string;
  series: Array<{ name: string; data: number[]; color?: string }>;
};

export type DataProject = {
  slug: string;
  title: string;
  description: string;
  hasEntry: boolean;
  charts?: SampleChartSpec[];
  entryFields?: DataEntryField[];
};

const yesNo = [
  { value: '', label: 'Select' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const DATA_PROJECTS: DataProject[] = [
  {
    slug: 'utilization',
    title: 'Utilization',
    description: 'Monthly trends for transfusions, discards, transfers, and redistributions.',
    hasEntry: false,
  },
  {
    slug: 'mhp-quality-metrics',
    title: 'MHP Quality Metrics',
    description: 'Massive hemorrhage protocol measures. Charts on this page use sample data.',
    hasEntry: true,
    charts: [
      {
        title: 'MHP activations',
        categories: ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
        yAxisTitle: 'Activations',
        series: [{ name: 'Activations', data: [12, 9, 15, 11, 14, 10], color: '#00467f' }],
      },
      {
        title: 'Median time to first component',
        categories: ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
        yAxisTitle: 'Minutes',
        series: [
          { name: 'First red cell', data: [8, 7, 9, 6, 8, 7], color: '#b32017' },
          { name: 'First plasma', data: [14, 12, 15, 11, 13, 12], color: '#f59e0b' },
        ],
      },
    ],
    entryFields: [
      { name: 'site', label: 'Hospital or site', type: 'text' },
      { name: 'month', label: 'Reporting month', type: 'month' },
      { name: 'activations', label: 'MHP activations', type: 'number' },
      { name: 'minutesToRedCell', label: 'Median minutes to first red cell', type: 'number' },
      { name: 'minutesToPlasma', label: 'Median minutes to first plasma', type: 'number' },
      { name: 'tranexamicAcid', label: 'Tranexamic acid given', type: 'select', options: yesNo },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    slug: 'bedside-audit-of-blood-administration',
    title: 'Bedside Audit of Blood Administration',
    description: 'Bedside transfusion audit results. Charts on this page use sample data.',
    hasEntry: true,
    charts: [
      {
        title: 'Audits meeting each check',
        categories: ['Patient identification', 'Two-person check', 'Vital signs'],
        yAxisTitle: 'Percent',
        series: [{ name: 'Met', data: [96, 91, 88], color: '#00467f' }],
      },
      {
        title: 'Audits by component',
        categories: ['Red cells', 'Plasma', 'Platelets', 'Cryoprecipitate'],
        yAxisTitle: 'Audits',
        series: [{ name: 'Audits', data: [42, 18, 15, 6], color: '#00467f' }],
      },
    ],
    entryFields: [
      { name: 'site', label: 'Hospital or site', type: 'text' },
      { name: 'auditDate', label: 'Audit date', type: 'date' },
      {
        name: 'component',
        label: 'Component',
        type: 'select',
        options: [
          { value: '', label: 'Select' },
          { value: 'red-cells', label: 'Red cells' },
          { value: 'plasma', label: 'Plasma' },
          { value: 'platelets', label: 'Platelets' },
          { value: 'cryoprecipitate', label: 'Cryoprecipitate' },
        ],
      },
      { name: 'patientId', label: 'Patient identification verified', type: 'select', options: yesNo },
      { name: 'twoPerson', label: 'Two-person check completed', type: 'select', options: yesNo },
      { name: 'vitals', label: 'Baseline vital signs recorded', type: 'select', options: yesNo },
      { name: 'reaction', label: 'Transfusion reaction observed', type: 'select', options: yesNo },
      { name: 'comments', label: 'Comments', type: 'textarea' },
    ],
  },
];

export function findDataProject(slug: string | undefined): DataProject | undefined {
  if (!slug) return undefined;
  return DATA_PROJECTS.find((project) => project.slug === slug);
}

export function dataProjectPath(slug: string): string {
  return `/data/${slug}`;
}

export function dataEntryPath(slug: string): string {
  return `/data/${slug}/entry`;
}
